/**
 * The call's diagnostics: what each peer connection is doing, read from
 * RTCPeerConnection.getStats() and from the remote tracks themselves.
 *
 * Track state comes first: on 30 Oct the phone (the native app's web view)
 * never rendered the coach's track while the desktop rendered the phone's,
 * so stats alone (bytes arriving) would have looked healthy. A track is
 * `attached` when the element showing it holds it, `live` until it ends,
 * and `producing` when frames (video: shown by the element) or samples
 * (audio) went up since the last sample. `muted` is shown but decides
 * nothing: Chromium keeps a remote track muted while its frames render.
 *
 * Pure, so it is unit tested: the overlay and the 10 s record read it.
 */

/** Where the page runs. The native app's web view registers `doubleBagel` (LiveWebView.swift). */
export type ClientShell = 'ios-app-webview' | 'ios-webview' | 'ios-safari' | 'safari' | 'chrome' | 'firefox' | 'other';

export function clientShell(ua: string, hasAppHandler: boolean): ClientShell {
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && /Mobile\//.test(ua));
  if (ios) {
    if (hasAppHandler) return 'ios-app-webview';
    // Safari's own UA carries "Safari/"; a WKWebView's does not (unless the app adds it).
    if (!/Safari\//.test(ua)) return 'ios-webview';
    if (/CriOS|FxiOS|EdgiOS/.test(ua)) return 'ios-webview';
    return 'ios-safari';
  }
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\/|Chromium\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

/** The counters a sample is worked out from: kept from one sample to the next. */
export interface StatsCounters {
  at: number;
  bytesIn: number;
  bytesOut: number;
  packetsIn: number;
  lostIn: number;
  framesDecoded: number;
  framesEncoded: number;
  audioSamples: number;
  /** Inbound video frames dropped, and seconds spent decoding, both totals (job 2, 3 Nov). */
  framesDropped?: number;
  decodeSeconds?: number;
}

export interface PeerSample {
  /** kbps, both directions, over the time since the last sample. */
  bitrateIn: number;
  bitrateOut: number;
  /** Percent of inbound packets lost since the last sample. */
  lossIn: number;
  /** The selected candidate pair's round trip, ms (null before one is chosen). */
  rttMs: number | null;
  fpsIn: number;
  fpsOut: number;
  /** Whether inbound video frames were decoded, and audio samples received, since the last sample. */
  videoFlowing: boolean;
  audioFlowing: boolean;
  /** Inbound video's size, as decoded. */
  frameW: number | null;
  frameH: number | null;
  /** Inbound video frames dropped per second, and ms of decoding per frame, since the last sample (null where the browser gives none). */
  dropIn: number | null;
  decodeMs: number | null;
  /** The camera this side sends: its size, and what the encoder says limits it (cpu, bandwidth, none). */
  outW: number | null;
  outH: number | null;
  outLimit: string | null;
  counters: StatsCounters;
}

type Stat = Record<string, unknown> & { type: string; id: string };
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * One sample from a getStats() report (its values, in any order) and the
 * counters of the one before, at `now` ms. The first sample has no rate.
 */
export function peerSample(stats: Iterable<Stat>, prev: StatsCounters | null, now: number): PeerSample {
  const c: StatsCounters = { at: now, bytesIn: 0, bytesOut: 0, packetsIn: 0, lostIn: 0, framesDecoded: 0, framesEncoded: 0, audioSamples: 0, framesDropped: 0, decodeSeconds: 0 };
  let hasDrops = false;
  let hasDecode = false;
  let outW: number | null = null;
  let outH: number | null = null;
  let outLimit: string | null = null;
  let rttMs: number | null = null;
  let frameW: number | null = null;
  let frameH: number | null = null;
  const all = [...stats];
  const byId = new Map(all.map((s) => [s.id, s]));
  let selectedPair: Stat | undefined;
  for (const s of all) {
    const kind = (s.kind ?? s.mediaType) as string | undefined;
    if (s.type === 'inbound-rtp') {
      c.bytesIn += num(s.bytesReceived);
      c.packetsIn += num(s.packetsReceived);
      c.lostIn += Math.max(0, num(s.packetsLost));
      if (kind === 'video') {
        c.framesDecoded += num(s.framesDecoded);
        if (num(s.frameWidth)) { frameW = num(s.frameWidth); frameH = num(s.frameHeight); }
        if (typeof s.framesDropped === 'number') { hasDrops = true; c.framesDropped! += num(s.framesDropped); }
        if (typeof s.totalDecodeTime === 'number') { hasDecode = true; c.decodeSeconds! += num(s.totalDecodeTime); }
      }
      if (kind === 'audio') c.audioSamples += num(s.totalSamplesReceived);
    } else if (s.type === 'outbound-rtp') {
      c.bytesOut += num(s.bytesSent);
      if (kind === 'video') {
        c.framesEncoded += num(s.framesEncoded);
        if (num(s.frameWidth) && (outW === null || num(s.frameWidth) > outW)) { outW = num(s.frameWidth); outH = num(s.frameHeight); }
        if (typeof s.qualityLimitationReason === 'string') outLimit = s.qualityLimitationReason;
      }
    } else if (s.type === 'transport' && typeof s.selectedCandidatePairId === 'string') {
      selectedPair = byId.get(s.selectedCandidatePairId);
    } else if (s.type === 'candidate-pair' && (s.selected === true || (s.nominated === true && s.state === 'succeeded')) && !selectedPair) {
      selectedPair = s;
    }
  }
  if (selectedPair && typeof selectedPair.currentRoundTripTime === 'number') rttMs = Math.round(selectedPair.currentRoundTripTime * 1000);
  const dt = prev ? (now - prev.at) / 1000 : 0;
  const rate = (a: number, b: number) => (dt > 0 ? Math.max(0, a - b) / dt : 0);
  const packets = prev ? Math.max(0, c.packetsIn - prev.packetsIn) : 0;
  const lost = prev ? Math.max(0, c.lostIn - prev.lostIn) : 0;
  return {
    bitrateIn: Math.round((rate(c.bytesIn, prev?.bytesIn ?? 0) * 8) / 1000),
    bitrateOut: Math.round((rate(c.bytesOut, prev?.bytesOut ?? 0) * 8) / 1000),
    lossIn: packets + lost > 0 ? Math.round((lost / (packets + lost)) * 1000) / 10 : 0,
    rttMs,
    fpsIn: Math.round(rate(c.framesDecoded, prev?.framesDecoded ?? 0)),
    fpsOut: Math.round(rate(c.framesEncoded, prev?.framesEncoded ?? 0)),
    videoFlowing: !!prev && c.framesDecoded > prev.framesDecoded,
    audioFlowing: !!prev && c.audioSamples > prev.audioSamples,
    frameW,
    frameH,
    dropIn: hasDrops && prev && prev.framesDropped !== undefined ? Math.round(rate(c.framesDropped!, prev.framesDropped) * 10) / 10 : null,
    decodeMs: hasDecode && prev && prev.decodeSeconds !== undefined && c.framesDecoded > prev.framesDecoded
      ? Math.round(((c.decodeSeconds! - prev.decodeSeconds) / (c.framesDecoded - prev.framesDecoded)) * 10000) / 10 : null,
    outW,
    outH,
    outLimit,
    counters: c,
  };
}

/**
 * Whether the element showed new video frames since the last sample, from
 * the best count the browser has: requestVideoFrameCallback's (frames
 * presented), else getVideoPlaybackQuality's total, but only once it has
 * ever moved (Safari leaves it at 0 for a camera stream, which read as "no
 * frames" over a live picture in the walk of 30 Oct), else inbound frames
 * decoded. Each count is [before, now], or null where there is none.
 */
export function videoShowing(p: { presented: [number, number] | null; quality: [number, number] | null; decoded: boolean }): boolean {
  if (p.presented) return p.presented[1] > p.presented[0];
  if (p.quality && p.quality[1] > 0) return p.quality[1] > p.quality[0];
  return p.decoded;
}

/** A remote track as the page sees it. */
export interface TrackState {
  /** There is such a track from the peer at all. */
  present: boolean;
  /** The element that shows it holds it (srcObject has the track). */
  attached: boolean;
  /** readyState 'live': not ended. */
  live: boolean;
  /** The track's own muted flag, as reported. */
  muted: boolean;
  /** Frames (video: the element's own count went up) or samples (audio: inbound stats) since the last sample. */
  producing: boolean;
}

export interface TrackInput {
  track: { readyState: string; muted: boolean; id: string } | null;
  /** The ids of the tracks in the element's srcObject, or null without one. */
  elementTrackIds: string[] | null;
  producing: boolean;
}

export function trackState(t: TrackInput): TrackState {
  if (!t.track) return { present: false, attached: false, live: false, muted: false, producing: false };
  return {
    present: true,
    attached: !!t.elementTrackIds?.includes(t.track.id),
    live: t.track.readyState === 'live',
    muted: t.track.muted,
    producing: t.producing,
  };
}

/** One line for a track: what is wrong with it first, or "ok". */
export function trackLine(s: TrackState): string {
  if (!s.present) return 'no track';
  if (!s.attached) return 'not attached';
  if (!s.live) return 'ended';
  if (!s.producing) return s.muted ? 'no frames (muted)' : 'no frames';
  return 'ok';
}

/**
 * The 10 s sample, on UI/UX's liveCallStats contract (docs/booking-contracts.md
 * 13): liveCallStats/{sessionId}_{uid}_{slot}, one per side per 10-second
 * slot, the measures of the connection to the lesson's other person. The
 * rule checks only `tracks`' own keys, so each track carries, beside its
 * enabled, muted and readyState, whether it is attached to the element and
 * producing frames (the 30 Oct case). `at` is the server's time, added by the
 * writer. The client (WKWebView or Safari) reads off the user agent: a
 * WKWebView's has no "Safari/" token.
 */
export const ICE_STATES = ['new', 'checking', 'connected', 'completed', 'failed', 'disconnected', 'closed'] as const;

export interface CallStatTrack { enabled: boolean; muted: boolean; readyState: string; attached: boolean; producing: boolean }

export interface CallStatSample {
  bookingId: string;
  sessionId: string;
  uid: string;
  side: 'coach' | 'player';
  slot: number;
  userAgent: string;
  iceState?: (typeof ICE_STATES)[number];
  bitrate?: { sendKbps: number; recvKbps: number };
  packetLoss?: { recvPct: number };
  rttMs?: number | null;
  fps?: { send: number; recv: number };
  tracks?: { audio?: CallStatTrack; video?: CallStatTrack & { w: number | null; h: number | null } };
}

export interface PeerForSample {
  ice: string;
  sample: PeerSample;
  video: TrackState & { enabled: boolean; readyState: string };
  audio: TrackState & { enabled: boolean; readyState: string };
}

export function callStatSample(p: {
  bookingId: string; sessionId: string; uid: string; side: 'coach' | 'player'; nowMs: number; userAgent: string; peer: PeerForSample | null;
}): { id: string; data: CallStatSample } {
  const slot = Math.floor(p.nowMs / 10_000);
  const data: CallStatSample = { bookingId: p.bookingId, sessionId: p.sessionId, uid: p.uid, side: p.side, slot, userAgent: p.userAgent.slice(0, 400) };
  const r = p.peer;
  if (r) {
    if ((ICE_STATES as readonly string[]).includes(r.ice)) data.iceState = r.ice as CallStatSample['iceState'];
    data.bitrate = { sendKbps: r.sample.bitrateOut, recvKbps: r.sample.bitrateIn };
    data.packetLoss = { recvPct: r.sample.lossIn };
    data.rttMs = r.sample.rttMs;
    data.fps = { send: r.sample.fpsOut, recv: r.sample.fpsIn };
    const t = (x: PeerForSample['audio']): CallStatTrack => ({ enabled: x.enabled, muted: x.muted, readyState: x.readyState, attached: x.attached, producing: x.producing });
    data.tracks = {
      ...(r.audio.present ? { audio: t(r.audio) } : {}),
      ...(r.video.present ? { video: { ...t(r.video), w: r.sample.frameW, h: r.sample.frameH } } : {}),
    };
  }
  return { id: `${p.sessionId}_${p.uid}_${slot}`, data };
}
